import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import PlusOutlined from '@ant-design/icons/PlusOutlined'

import { LntButton, LntForm } from '@app/components/base'
import { ExtendedTable } from '@app/components'
import { CommonConfirmDeleteExternalProduct } from '@app/modals'
import { Show } from '@app/components/util'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { EXTERNAL_PRODUCTS_CATALOG_KEY } from '@app/constants/queryKeys'

import {
  deleteExternalProductsLink,
  deleteExternalProductsUnlink,
  getDependentProducts
} from '@app/api/routes/externalProductsCatalog'

import { getExternalDependentProductsFromResponse } from '@app/utils/responseAdapters'
import { isEmptyString } from '@app/utils'

import { useNotification, usePaginationParams, useToggle } from '@app/hooks'

import { useDependentProductColumn } from './hooks'
import { getColumns } from './utils'

import { CharacteristicsGroup, TopInfoProduct, TreeShopGroup } from './components'

import styles from './styles.module.scss'

import type { DependentProductsDataSource } from '@app/types/dataSource'
import type { ExternalProductsListItemFormData, ProductCardFormProps } from './types'

const columns = getColumns()

export const ProductCardForm: React.FC<ProductCardFormProps> = props => {
  const { formId, id, dataSource, onSubmit, onShowProductLentaDrawer, onShowNotConfirmedDrawer } = props

  const linkLentaConfirmModalToggle = useToggle()
  const linkConfirmModalToggle = useToggle()

  const [chosenDeleteDependentProduct, setChosenDeleteDependentProduct] = useState<DependentProductsDataSource | null>(
    null
  )

  const isMatchedWithProduct = useMemo(() => dataSource?.isMatchedWithProduct ?? false, [dataSource])

  const { pagination, setPagination, paginationParams } = usePaginationParams()
  const form = useForm<ExternalProductsListItemFormData>()
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { data: dependentProductsListData, isLoading: isdependentProductsListDataLoading } = useQuery({
    enabled: !isEmptyString(id),
    queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.dependentProductsList],
    queryFn: async () => await getDependentProducts({ ...paginationParams, id })
  })

  const deleteExternalProductsLinkMutation = useMutation({
    mutationFn: deleteExternalProductsLink,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.dependentProductsList] })
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })

      notification.success({
        message: 'Связь успешна удалена'
      })

      linkLentaConfirmModalToggle.setUntoggled()
    }
  })

  const deleteExternalProductsUnlinkMutation = useMutation({
    mutationFn: deleteExternalProductsUnlink,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.dependentProductsList] })
      await queryClient.invalidateQueries({ queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsListItem] })

      notification.success({
        message: 'Внешний товар успешно удален из связи'
      })

      linkConfirmModalToggle.setUntoggled()

      setChosenDeleteDependentProduct(null)
    }
  })

  const dependentProductsDataSource = useMemo(
    () => getExternalDependentProductsFromResponse(dependentProductsListData),
    [dependentProductsListData]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleDeleteExternalProductsLink = (): void => {
    const productId = dataSource?.generalProduct?.key

    if (!id || !productId) return

    deleteExternalProductsLinkMutation.mutate({ id, productId })
  }

  const handleDeleteExternalProductsUnlinkMutation = (): void => {
    const id = chosenDeleteDependentProduct?.key

    if (!id) return

    deleteExternalProductsUnlinkMutation.mutate({ id })
  }

  const handleClickTableDelete = (dependentProduct: DependentProductsDataSource): void => {
    setChosenDeleteDependentProduct(dependentProduct)

    linkConfirmModalToggle.setToggled()
  }

  useDependentProductColumn<DependentProductsDataSource>({
    columns,
    onClickDelete: handleClickTableDelete
  })

  return (
    <LntForm<ExternalProductsListItemFormData>
      layout='vertical'
      formInstance={ form }
      id={ formId }
      onSubmit={ onSubmit }
      loading={ !dataSource }
    >
      <div className={ styles.productCardForm__containerWrapper }>
        <TopInfoProduct
          dataSource={ dataSource }
          disabled={ isMatchedWithProduct }
          onShowLentaConfirmModalToggle={ linkLentaConfirmModalToggle.setToggled }
          onShowProductLentaDrawer={ onShowProductLentaDrawer }
        />

        <TreeShopGroup
          dataSource={ dataSource }
          disabled={ isMatchedWithProduct }
        />

        <CharacteristicsGroup
          dataSource={ dataSource }
          disabled={ isMatchedWithProduct }
        />

        <Show condition={ !!dataSource?.isConfirmed }>
          <div className={ styles.productCardForm__mergeWrapper }>
            <p>Объединение товаров</p>

            <LntButton
              type={ BUTTON_TYPE.link }
              size={ SIZE.middle }
              onClick={ onShowNotConfirmedDrawer }
            >
              <PlusOutlined />
              Внешние товары
            </LntButton>
          </div>

          <ExtendedTable<DependentProductsDataSource>
            loading={ isdependentProductsListDataLoading }
            columns={ columns }
            dataSource={ dependentProductsDataSource }
            pagination={ {
              ...pagination,
              total: dependentProductsListData?.count,
              onChange: handlePaginationChange
            } }
          />
        </Show>
      </div>

      <CommonConfirmDeleteExternalProduct
        title={ `Вы уверены, что хотите удалить связь с товаром: ${dataSource?.name ?? ''}?` }
        open={ linkLentaConfirmModalToggle.value }
        onCancel={ linkLentaConfirmModalToggle.setUntoggled }
        onSubmit={ handleDeleteExternalProductsLink }
        cancelText='Отмена'
        okText='Ок'
      />

      <CommonConfirmDeleteExternalProduct
        title={ `Вы уверены, что хотите удалить связь с товаром: ${chosenDeleteDependentProduct?.name ?? ''}?` }
        open={ linkConfirmModalToggle.value }
        onCancel={ linkConfirmModalToggle.setUntoggled }
        onSubmit={ handleDeleteExternalProductsUnlinkMutation }
        cancelText='Отмена'
        okText='Ок'
      />
    </LntForm>
  )
}
